import React from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import * as R from 'ramda';

import BackIcon from 'material-ui/svg-icons/navigation/arrow-back';

import { Section, SectionItem } from '../basic/section';
import Button from '../basic/Button';
import TextField from '../basic/TextField';
import Toggle from '../basic/Toggle';
import SelectField from '../basic/SelectField';

import { fetchAccountGroupIdByName } from '../../services/Api';
import { LANGUAGES, PRICE_PACKAGES } from '../../constants/Constants';

import './Form.css';

const Item = ({ children, empty }) => (
  <span style={{ fontSize: '14px', ...(empty ? { color: 'rgba(33,33,33,.54)' } : {}) }}>{children}</span>
);

const renderPackages = ({ fields }) => {
  return (
    <div className="row">
      {fields.map((p, idx) => {
        const fld = fields.get(idx);
        const discounted = !isNaN(parseFloat(fld.creditPriceDiscount));
        let warn = null;
        if (isNaN(parseFloat(fld.creditPrice))
          || isNaN(parseFloat(fld.creditAmount))
          || isNaN(parseFloat(fld.expiryInMonths))) {
          warn = 'This package is incomplete and will not be shown to the user';
        }
        const vals = {
          creditAmount: parseFloat(fld.creditAmount) || 0,
          creditPrice: parseFloat(fld.creditPrice) || 0,
          creditPriceDiscount: parseFloat(fld.creditPriceDiscount) || 0
        };
        return (
          <div key={fld.id} className="col-sm-6 col-md-3">
            <strong>{fld.label}</strong>
            <Field name={`${p}.creditAmount`} type="number" component={TextField} label="Credit amount" fullWidth />
            <Field
              name={`${p}.creditPrice`}
              type="number"
              component={TextField}
              label="Price per credit in €"
              fullWidth
            />
            <Field
              name={`${p}.creditPriceDiscount`}
              type="number"
              component={TextField}
              label="Discount in €"
              helperText="May be left empty"
              fullWidth
            />
            <Field
              name={`${p}.expiryInMonths`}
              type="number"
              component={TextField}
              label="Expiry in months"
              fullWidth
            />
            <div className="Text-regular">
              <span className="floating-label">Available profiles (TOD)</span>
              <div>{(vals.creditAmount / 5).toFixed(0)}</div>
            </div>
            <div className="Text-regular">
              <span className="floating-label">Package price</span>
              <div>
                {discounted ? (
                  <s>{(vals.creditAmount * vals.creditPrice).toFixed(2)} €</s>
                ) : (
                  <strong>{(vals.creditAmount * vals.creditPrice).toFixed(2)} €</strong>
                )}
              </div>
            </div>
            {discounted && (
              <div className="Text-regular">
                <span className="floating-label">Discounted package price</span>
                <div>
                  <strong>{(vals.creditAmount * (vals.creditPrice - vals.creditPriceDiscount)).toFixed(2)} €</strong>
                </div>
              </div>
            )}
            {warn && <span className="Text-small Text-warn" style={{ marginTop: '16px' }}>{warn}</span>}
          </div>
        );
      })}
    </div>
  );
};

const Form = props => {
  const { submitting, customPriceListEnabled } = props;
  const currentGroup = props.currentGroup || {};
  const { handleSubmit } = props;
  const isNew = !currentGroup.id;
  const companies = currentGroup.companies || [];
  const admins = currentGroup.admins || [];
  return (
    <form className="Form" onSubmit={handleSubmit}>
      <ScrollArea smoothScrolling>
        <div className="Form-content-container">
          <div className="container">
            <div className="row">
              <div className="col-sm-12">
                <Section title="General info">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field name="name" component={TextField} label="Name" fullWidth />
                  </SectionItem>
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="defaultForLanguage"
                      component={SelectField}
                      label="Default for language"
                      fullWidth
                      items={[{ label: 'None', value: null }, ...LANGUAGES]}
                    />
                  </SectionItem>
                </Section>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <Section title="Price list">
                  <SectionItem>
                    <Field
                      name="customPriceListEnabled"
                      component={Toggle}
                      label="Enable custom price list"
                      fullWidth
                      helperText="The clients with accounts within the group will see this price list. Otherwise no price list will be shown to them."
                    />
                  </SectionItem>
                  {customPriceListEnabled && (
                    <SectionItem>
                      <FieldArray name="customPricePackages" component={renderPackages} />
                    </SectionItem>
                  )}
                </Section>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Section title="Companies">
                  {R.map(
                    c => (
                      <SectionItem key={c.id}>
                        <Item>
                          {c.name} ({c.slug})
                        </Item>
                      </SectionItem>
                    ),
                    companies
                  )}
                  {companies.length === 0 && (
                    <SectionItem>
                      <Item empty>No companies</Item>
                    </SectionItem>
                  )}
                </Section>
              </div>
              <div className="col-md-6">
                <Section title="Local admins">
                  {R.map(
                    a => (
                      <SectionItem key={a.id}>
                        <Item>
                          {a.lastName} {a.firstName} ({a.email})
                        </Item>
                      </SectionItem>
                    ),
                    admins
                  )}
                  {admins.length === 0 && (
                    <SectionItem>
                      <Item empty>No local admins</Item>
                    </SectionItem>
                  )}
                </Section>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
      <div className="Form-buttons-container">
        <div className="Form-buttons clearfix">
          <div className="float-left" style={{ fontSize: 0 }}>
            <Button
              label="Back"
              icon={<BackIcon />}
              style={{ fontSize: '16px' }}
              containerElement={<Link to="/settings/accounts/groups" />}
            />
          </div>
          <div className="float-right">
            <Button raised primary label={isNew ? 'Create' : 'Save'} type="submit" loading={submitting} />
          </div>
        </div>
      </div>
    </form>
  );
};

const selector = formValueSelector('accountGroup');
const mapStateToProps = state => {
  const { entities: { accountGroups: { currentGroup, currentGroupSaving } }, auth } = state;
  return {
    auth,
    saveInProgress: currentGroupSaving,
    customPriceListEnabled: selector(state, 'customPriceListEnabled'),
    initialValues: {
      ...currentGroup,
      customPricePackages: R.map(
        p => ({
          ...p,
          ...((currentGroup &&
            currentGroup.customPriceList &&
            currentGroup.customPriceList.packages &&
            currentGroup.customPriceList.packages[p.id]) ||
            {})
        }),
        PRICE_PACKAGES
      )
    }
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'accountGroup',
    enableReinitialize: true,
    validate: values => {
      const errors = {};
      if (!values.name) {
        errors.name = 'Name is required';
      } else if (values.name.length < 3) {
        errors.name = 'At least 3 characters are required';
      }
      if (values.customPriceListEnabled) {
        const cppErrors = [];
        for (let i = 0; i < values.customPricePackages.length; i += 1) {
          const pkg = values.customPricePackages[i];
          const err = {};
          if (parseFloat(pkg.creditPrice) <= 0) {
            err.creditPrice = 'Price must be a positive number';
          }
          if (parseFloat(pkg.creditAmount) <= 0) {
            err.creditAmount = 'Amount must be positive';
          }
          if (parseFloat(pkg.creditPriceDiscount) <= 0) {
            err.creditPriceDiscount = 'Discount must be a positive number';
          }
          if (parseFloat(pkg.expiryInMonths) <= 0) {
            err.expiryInMonths = 'Expiry must be a positive number';
          }
          if (!R.isEmpty(err)) {
            cppErrors[i] = err;
          }
        }
        if (cppErrors.length) {
          errors.customPricePackages = cppErrors;
        }
      }
      return errors;
    },
    warn: values => {
      const warns = {};
      if (values.customPriceListEnabled) {
        const cppWarns = [];
        const pkgCount = values.customPricePackages.length;
        const numberOfDiscountedPkgs = R.reduce((c, p) => c + (isNaN(parseFloat(p.creditPriceDiscount)) ? 0 : 1), 0, values.customPricePackages);
        const warnAboutDiscount = 0 < numberOfDiscountedPkgs && numberOfDiscountedPkgs < pkgCount;
        for (let i = 0; i < pkgCount; i += 1) {
          const pkg = values.customPricePackages[i];
          // check discounts
          const pkgWarns = {};
          if (warnAboutDiscount) {
            if (isNaN(parseFloat(pkg.creditPriceDiscount))) {
              pkgWarns.creditPriceDiscount = 'Other packages have discount defined';
            } else {
              pkgWarns.creditPriceDiscount = 'Some packages don\'t have discount defined';
            }
          }
          // check internal logic
          if (i > 0) {
            const prevPkg = values.customPricePackages[i - 1];
            if (parseFloat(pkg.creditPrice) >= parseFloat(prevPkg.creditPrice)) {
              pkgWarns.creditPrice = 'Price should probably be lower than in the previous package';
            }
            if (parseFloat(pkg.creditAmount) <= parseFloat(prevPkg.creditAmount)) {
              pkgWarns.creditAmount = 'Number of credits should probably be higher than in the previous package';
            }
            if (parseFloat(pkg.expiryInMonths) < parseFloat(prevPkg.expiryInMonths)) {
              pkgWarns.expiryInMonths = 'Expiry should probably not be lower than in the previous package';
            }
          }
          if (i < pkgCount - 1) {
            const nextPkg = values.customPricePackages[i + 1];
            if (parseFloat(pkg.creditPrice) <= parseFloat(nextPkg.creditPrice)) {
              pkgWarns.creditPrice = 'Price should probably be higher than in the next package';
            }
            if (parseFloat(pkg.creditAmount) >= parseFloat(nextPkg.creditAmount)) {
              pkgWarns.creditAmount = 'Number of credits should probably be lower than in the next package';
            }
            if (parseFloat(pkg.expiryInMonths) > parseFloat(nextPkg.expiryInMonths)) {
              pkgWarns.expiryInMonths = 'Expiry should probably not be higher than in the next package';
            }
          }
          // check full definition
          if (isNaN(parseFloat(pkg.creditPrice))) {
            pkgWarns.creditPrice = 'Credit price should be defined for a complete package';
          }
          if (isNaN(parseFloat(pkg.creditAmount))) {
            pkgWarns.creditAmount = 'Credit amount should be defined for a complete package';
          }
          if (isNaN(parseFloat(pkg.expiryInMonths))) {
            pkgWarns.expiryInMonths = 'Expiry should be defined for a complete package';
          }
          if (!R.isEmpty(pkgWarns)) {
            cppWarns[i] = pkgWarns;
          }
        }
        if (cppWarns.length) {
          warns.customPricePackages = cppWarns;
        }
      }
      return warns;
    },
    asyncValidate: (values, dispatch, props, field) => {
      const prevErrors = props.asyncErrors;
      if (field !== 'name') {
        if (prevErrors) {
          return Promise.reject(prevErrors);
        }
        return Promise.resolve();
      }
      return new Promise((res, rej) => {
        fetchAccountGroupIdByName(values.name.trim(), props.auth.access_token).then(({ body: { payload } }) => {
          if (payload && (!props.currentGroup || payload !== props.currentGroup.id)) {
            return rej({ ...prevErrors, name: 'Group with given name already exists' });
          }
          return res();
        });
      });
    },
    asyncBlurFields: ['name']
  })(Form)
);



// WEBPACK FOOTER //
// ./src/components/forms/AccountGroupForm.js